import { IAnalyticsEvent } from '@verv-inc/js-toolkit/core-analytics';
import { getParameterFromURL } from '../../scripts';
import { getInternalProductIdFromName } from '../../scripts/getInternalProductIdFromName';
import getProductDuration from '../../scripts/getProductDuration';
import { determinePaymentVersion } from '../../scripts/paymentVersion';
import getCurrencyValue from '../../scripts/getCurrencyValue';
import { getInternalPDFId } from '../../scripts/getInternalPDFId';
import { eLTVChecker, eLTVLocalChecker } from '../../scripts/eLTVChecker';

const FunnelOnOnboardingEventName = 'quiz_funnel';
const abTestEventName = 'AB_TEST';
const everydoggyV1 = getParameterFromURL('everydoggyV1');
let paymentVersion;
const paymentOfferVersion = 'version1_with_offer';

if(everydoggyV1) {
  paymentVersion = 'version1';
}

interface GTMOnbFunnelEventTryPaidParameters {
  paymentType: string;
}

interface GTMOnbFunnelEventQuestOnOfferParameters {
  paymentType: string;
}

interface GTMOnbFunnelEventPaymentDeclineParameters {
  payment_system: string;
  product_name: string;
  decline_reason: string;
}
interface GTMOnbFunnelEventPaymentAttemptClosedParameters {
  payment_system: string;
  product_name: string;
}

interface GTMOnbFunnelEventHardwareParameters {
  productNames: string[];
  email?: string;
}

interface GTMOnbFunnelEventSupplementsParameters {
  productNames: string[];
  count: number;
  price: number;
  email?: string;
}

type PaywallSource = 'cancel' | 'paypal_return';

const resolvePaywallSource = (source?: PaywallSource): PaywallSource | undefined => {
  if (source) {
    return source;
  }

  const redirectStatus = getParameterFromURL('paypal_redirect_status');
  if (redirectStatus === 'cancel') {
    return 'cancel';
  }
  if (redirectStatus === 'return') {
    return 'paypal_return';
  }

  try {
    const sourceFromSession = window.sessionStorage.getItem('paypal_show_payment_page_source');
    if (sourceFromSession === 'cancel' || sourceFromSession === 'paypal_return') {
      window.sessionStorage.removeItem('paypal_show_payment_page_source');
      return sourceFromSession;
    }
  } catch (_) {
    void 0;
  }

  return undefined;
};

export const GTMOnboardingFunnelEvent = {
  showPayment: (product: string, currency: string, value: number, productDuration: string): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'open_checkout',
        product_name: product,
        currency: currency,
        final_value: value,
        section: window.clickedPlanBlock,
        product_duration: productDuration,
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  showPaywall: (version?: string, source?: PaywallSource): IAnalyticsEvent => {
    const paywallSource = resolvePaywallSource(source);
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_payment_page',
        payment_version: version ? version : paymentVersion,
        payment_type: determinePaymentVersion() || 'p13',
        currency: getCurrencyValue(),
        ...(paywallSource ? { source: paywallSource } : {}),
      }
    };
  },
  showOfferPaywall: (version?: string, source?: PaywallSource): IAnalyticsEvent => {
    const paywallSource = resolvePaywallSource(source);
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_payment_page',
        payment_version: version ? version : paymentOfferVersion,
        currency: getCurrencyValue(),
        payment_type: determinePaymentVersion() || 'unknown',
        ...(paywallSource ? { source: paywallSource } : {}),
      }
    };
  },
  showSpecialOffer: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_special_offer',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  chooseSpecialOffer: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'choose_special_offer',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  tryPaid: (parameters: GTMOnbFunnelEventTryPaidParameters): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'try paid',
        payment_type: parameters.paymentType,
      }
    };
  },
  paymentDecline: (parameters: GTMOnbFunnelEventPaymentDeclineParameters): IAnalyticsEvent => {
    const offerType = window.showSpecialOffer ? 'special_offer' : 'main_offer';
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'payment_decline',
        payment_system: parameters.payment_system,
        product_name: parameters.product_name,
        decline_reason: parameters.decline_reason,
        product_duration: getProductDuration(parameters.product_name),
        'offer_type': offerType,
        currency: getCurrencyValue(),
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  paymentAttemptClosed: (parameters: GTMOnbFunnelEventPaymentAttemptClosedParameters): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'payment_attempt_closed',
        payment_system: parameters.payment_system,
        product_name: parameters.product_name,
        product_duration: getProductDuration(parameters.product_name),
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  goToAccount: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'go_account',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  showPdfUpsale: (version: string, contentIdsPdf: string[]): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_pdf_upsale',
        pdf_version: version,
        content_ids_pdf: contentIdsPdf,
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  paymentIntentionPdf: (product: string, type: string, system: string, productAmount: string, productIdActive: number): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'payment_intention_pdf',
        pdf_product: product,
        pdf_type: type,
        payment_system: system,
        value: Number(productAmount),
        payment_type: determinePaymentVersion() || 'unknown',
        currency: getCurrencyValue(),
        product_name: getInternalPDFId(productIdActive),
      }
    };
  },
  paymentSuccessfulPdf: (product: string, type: string, system: string, productAmount: string, productIdActive: number): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'payment_successful_pdf',
        pdf_product: product,
        pdf_type: type,
        payment_system: system,
        value:  Number(productAmount),
        pdf_version: `${type}_${productAmount}`,
        payment_type: determinePaymentVersion() || 'unknown',
        product_name: getInternalPDFId(productIdActive),
      }
    };
  },
  getContentIds: (ids: string[], emailInputValue: string): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'get_content_ids',
        content_ids: ids ? ids : [],
        email: emailInputValue,
      }
    };
  },
  addToCart: (product: string, currency: string, value: number): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'add_to_cart',
        product_name: getInternalProductIdFromName(product),
        currency: currency,
        final_value: value,
      }
    };
  },
  abTest: (abName: string, abGroup: string): IAnalyticsEvent => {
    return {
      name: abTestEventName,
      info: {
        ab_name: abName,
        ab_group: abGroup,
        ab_test_type: 'payment_page',
      }
    };
  },
  showChatUpsale: (contentIdsChat: string[]): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_chat_upsale',
        content_ids_chat: contentIdsChat,
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  showHardwareUpsale: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_hardware_upsale',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  closeHardwareUpsale: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'close_hardware_upsale',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  paymentIntentionHardware: (parameters: GTMOnbFunnelEventHardwareParameters): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'payment_intetion_hardware',
        product_name: parameters.productNames,
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  showHardwareUpsalePopup: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_hardware_upsale_popup',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  closeHardwareUpsalePopup: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'close_hardware_upsale_popup',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  subscribeHardware: (parameters: GTMOnbFunnelEventHardwareParameters): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'subscribe_hardware',
        product_name: parameters.productNames,
        email: parameters.email || '',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  showSupplementsUpsale: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_supplements_upsale',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  closeSupplementsUpsale: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'close_supplements_upsale',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  productSelectedSupplements: (productName: string): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'product_selected_supplements',
        product_name: productName,
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  paymentIntentionSupplements: (parameters: GTMOnbFunnelEventSupplementsParameters): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'payment_intetion_supplements',
        product_name: parameters.productNames,
        count: parameters.count,
        price: parameters.price,
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  showSupplementsUpsalePopup: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'show_supplements_upsale_popup',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  closeSupplementsUpsalePopup: (): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'close_supplements_upsale_popup',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  subscribeSupplements: (parameters: GTMOnbFunnelEventSupplementsParameters): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'subscribe_supplements',
        product_name: parameters.productNames,
        count: parameters.count,
        price: parameters.price,
        email: parameters.email || '',
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  paymentIntentionChat: (product: string, system: string, productAmount: string): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'payment_intention_chat',
        chat_product: product,
        payment_system: system,
        value: Number(productAmount),
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  paymentSuccessfulChat: (product: string, system: string, productAmount: string): IAnalyticsEvent => {
    return {
      name: FunnelOnOnboardingEventName,
      info: {
        quiz_action: 'payment_successful_chat',
        chat_product: product,
        product_name: product,
        payment_system: system,
        value:  Number(productAmount),
        payment_type: determinePaymentVersion() || 'unknown',
        eLTV: eLTVChecker(product),
        eLTV_local_value: eLTVLocalChecker(product),
      }
    };
  },
  paypalError: (errorName: any, email: string, productName: string) : IAnalyticsEvent => {
    return {
      name: 'paypal_error',
      info: {
        error_name: errorName,
        email: email,
        product_name: productName,
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  blockScroll: (blockName: string, percent: any, bucket: any, paymentType: string) : IAnalyticsEvent => {
    return {
      name: 'block_scroll_depth',
      info: {
        block_name: blockName,
        scroll_percent: percent,
        scroll_bucket: bucket,
        payment_type: determinePaymentVersion() || 'unknown',
      }
    };
  },
  paypalRegisterAttempt: (params: {
    subscriptionId: string;
    productName: string;
  }): IAnalyticsEvent => ({
    name: 'paypal_register_attempt',
    info: {
      subscription_id: params.subscriptionId,
      product_name: params.productName,
      payment_type: determinePaymentVersion() || 'unknown',
    },
  }),
  paypalRegisterSuccess: (params: {
    subscriptionId: string;
    productName: string;
    httpStatus: number;
  }): IAnalyticsEvent => ({
    name: 'paypal_register_success',
    info: {
      subscription_id: params.subscriptionId,
      product_name: params.productName,
      http_status: params.httpStatus,
      payment_type: determinePaymentVersion() || 'unknown',
    },
  }),
   paypalRegisterFailure: (params: {
    subscriptionId: string;
    productName: string;
    httpStatus: number;
    errorBody: string;
  }): IAnalyticsEvent => ({
    name: 'paypal_register_failure',
    info: {
      subscription_id: params.subscriptionId,
      product_name: params.productName,
      http_status: params.httpStatus,
      error_body: params.errorBody,
      payment_type: determinePaymentVersion() || 'unknown',
    },
  }),
};
